import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  FaCircleCheck,
  FaClock,
  FaCircleXmark,
} from "react-icons/fa6";
import { PrimaryButton } from "./Buttons.jsx";

const checklistItems = [
  {
    key: "legal",
    label: "Sign Merchant Agreement",
    description:
      "We'll email your merchant agreement to sign. Complete it to authorize Saucey to onboard your store.",
  },
  {
    key: "stripe",
    label: "Connect Stripe Account",
    description:
      "Set up your Stripe Connect account so we can pay out your sales.",
  },
  {
    key: "licenses",
    label: "Upload Licenses",
    description:
      "Provide the licenses required for the age-restricted products you sell.",
  },
  {
    key: "menu",
    label: "Upload Your Menu",
    description:
      "Upload a CSV of your product catalog so we can populate your store.",
  },
];

const STEP_ORDER = checklistItems.map((i) => i.key);

function stepStatus(currentStep, itemKey) {
  if (!currentStep) return "in_progress";
  if (currentStep === "under_review") return "completed";
  const currentIdx = STEP_ORDER.indexOf(currentStep);
  const itemIdx = STEP_ORDER.indexOf(itemKey);
  if (currentIdx === -1 || itemIdx === -1) return undefined;
  if (itemIdx < currentIdx) return "completed";
  if (itemIdx === currentIdx) return "in_progress";
  return undefined;
}

const MarketplaceOnboardingChecklist = () => {
  const navigate = useNavigate();
  const { selectedLocation } = useSelector((state) => state.app);

  const isUnderReview =
    selectedLocation?.onboarding === true &&
    selectedLocation?.onboarding_step === "under_review";
  const isLive = selectedLocation?.onboarding === false;

  const stepStatuses = useMemo(() => {
    const out = {};
    checklistItems.forEach((item) => {
      out[item.key] = stepStatus(selectedLocation?.onboarding_step, item.key);
    });
    return out;
  }, [selectedLocation?.onboarding_step]);

  const goToWizard = () => navigate("/marketplace-onboarding");

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
      <div className="sm:flex sm:justify-between sm:items-center mb-8">
        <div className="mb-4 sm:mb-0">
          <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
            Marketplace Onboarding
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2">
            Complete these steps to finish setting up your Saucey Marketplace store.
          </p>
        </div>
      </div>

      {isUnderReview && (
        <div className="shadow-lg rounded-sm border p-6 mb-6 bg-white dark:bg-theme-surface-accent border-slate-200 dark:border-slate-700">
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
              <FaClock className="w-5 h-5 text-yellow-500 dark:text-yellow-400" />
              We are reviewing your submission
            </h2>
            {selectedLocation?.last_step_completed && (
              <p className="text-xs text-slate-600 dark:text-slate-400">
                Submitted on{" "}
                {new Date(selectedLocation.last_step_completed).toLocaleString()}
              </p>
            )}
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Approval time may vary based on the complexity of the submission.
              You can check back on this page for updates — we'll also email you
              when the status changes.
            </p>
          </div>
        </div>
      )}

      {isLive && (
        <div className="shadow-lg rounded-sm border p-6 mb-6 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-green-600 dark:text-green-400 flex items-center gap-2">
              <FaCircleCheck className="w-5 h-5 text-green-600 dark:text-green-400" />
              Your store is live
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              You're approved and live on Saucey Marketplace. Welcome aboard.
            </p>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-theme-surface-accent shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 p-6">
        <div className="space-y-4">
          {checklistItems.map((item) => {
            const status = stepStatuses[item.key];
            const isCompleted = status === "completed";
            const isInProgress = status === "in_progress";
            const isDisabled = isUnderReview || isLive;

            return (
              <button
                key={item.key}
                type="button"
                onClick={goToWizard}
                disabled={isDisabled}
                className={`w-full flex items-start gap-3 p-4 border border-slate-200 dark:border-slate-700 rounded-lg transition-colors text-left ${
                  isDisabled
                    ? "opacity-60 cursor-not-allowed bg-slate-50 dark:bg-slate-800/30"
                    : "hover:bg-slate-50 dark:hover:bg-slate-800/50"
                }`}
              >
                <div className="flex-shrink-0 mt-0.5">
                  <FaCircleCheck
                    className={`w-5 h-5 ${
                      isCompleted
                        ? "text-green-600 dark:text-green-400"
                        : isInProgress
                        ? "text-yellow-500 dark:text-yellow-400"
                        : "text-slate-300 dark:text-slate-600"
                    }`}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 dark:text-slate-200">
                    {item.label}
                    {isInProgress && (
                      <span className="ml-2 text-xs text-yellow-600 dark:text-yellow-400 font-normal">
                        (In Progress)
                      </span>
                    )}
                  </div>
                  {item.description && (
                    <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                      {item.description}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {!isUnderReview && !isLive && (
          <div className="pt-6 mt-6 border-t border-slate-200 dark:border-slate-700">
            <PrimaryButton onClick={goToWizard} width="full">
              Continue Onboarding
            </PrimaryButton>
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketplaceOnboardingChecklist;
